import { IAggregationsResult, IRowData } from "@multiweb/types/contracts";
import { IBigNumber } from "Global/Base/DataTypes/IBigNumber";
import { IComparable } from "Global/Base/IComparable";
import { RequestContext } from "OW.Remoting/Remoting/RequestContext";

/** Typ hodnoty parametru podmínky */
export type ConditionParam = IComparable | boolean | number | string | null;

/** Typ kolekce s hodnotami pro parametry podmínky */
export type ConditionParams = readonly ConditionParam[] | null | undefined;

/**
 * Výčet dostupných agregačních funkcí.
 * @see {@link IDataReaderClient#getAggregation}
 * @includeToDoc
 */
export enum AggregationFunction {
    count = "COUNT",
    min = "MIN",
    max = "MAX",
    avg = "AVG",
    sum = "SUM",
}

/**
 * Rozhraní pro parametry specifikující chování načítání řádků dat
 * @includeToDoc
 */
export interface IDataRowsParams {
    /**
     * Pole parametrů, které jsou pak odkazovány v podmínce pomocí @indexVPoli (@0)
     */
    readonly params?: ConditionParams;
    /**
     * Pole parametrů, podle kterých se mají řadit výsledky dotazu
     */
    readonly orderBy?: readonly string[];
    /**
     * Pole parametrů, podle kterých se mají seskupovat výsledky dotazu
     */
    readonly groupBy?: readonly string[];
    /**
     * Index (pořadové číslo), od kterého záznamu se mají vrátit záznamy, defaultně 0
     */
    readonly startIndex?: number;
    /**
     * Maximum vrácených záznamů, defaultně -1 (neomezeno)
     */
    readonly count?: number;
    /**
     * Maximální délka dat jednotlivých atributů (ořežou se), defaultně 0 (ořežou se na nakonfigurovanou hodnotu TrimLength v konfiguraci služby), -1 pro neomezeně
     */
    readonly dataLength?: number;
    /**
     * Pouze unikátní záznamy
     */
    readonly distinct?: boolean;
    /**
     * Příznak, zda se mají číst i verzované záznamy
     */
    readonly readVersioned?: boolean;
}

/**
 * Vstupní parametry pro hromadnou agregaci atributů jedné třídy.
 * Výsledky agregací jsou zapsány k odpovídajícím klíčům z těchto vstupních parametrů.
 */
export interface IAggregationsParams {
    readonly [key: string]: AggregationParams;
}

/**
 * Vstupní parametry pro agregaci jednoho atributu třídy.
 */
export type AggregationParams = {
    /** Název atributu, který se agreguje */
    readonly attributeName: string;
    /** Agregační funkce určující způsob agregace */
    readonly aggregationFunction: AggregationFunction;
};

/**
 * Klient pro čtení aplikačních dat a agregačních funkcí nad nimi.
 * @includeToDoc
 */
export interface IDataReaderClient {

    /**
     * Získá výsledek agregační funkce použité nad zadaným atributem v záznamech třídy splňující omezující podmínku. Pokud atribut v odpovídajících záznamech neobsahuje žádná data, vrací hodnotu 0.
     *
     * @param aggregationFunction Agregační funkce určující způsob agregace
     * @param className Název třídy, jejíž atribut se agreguje
     * @param attributeName Název atributu, který se agreguje
     * @param condition Podmínka omezující agregovaná data
     * @param conditionParams Parametry pro podmínku omezující agregovaná data
     * @param readVersioned Příznak, zda se má agregace provádět i nad verzovanými záznamy
     * @param requestContext Viz {@link RequestContext}
     * @see AggregationFunction
     * @see getAggregations
     */
    getAggregation(aggregationFunction: AggregationFunction, className: string, attributeName: string, condition: string, conditionParams?: ConditionParams, readVersioned?: boolean, requestContext?: RequestContext): Promise<IBigNumber>;

    /**
     * Získá výsledek agregačních funkcí nad různými atributy v záznamech třídy splňující omezující podmínku. Pro atributy, které v odpovídajících záznamech neobsahují žádná data, vrací hodnotu 0.
     * @param aggregationParams Agregační parametry definující jaké atributy se mají agregovat a jaké agregační funkce se pro to použijí.
     * @param className Název třídy, jejíž atribut se agreguje
     * @param condition Podmínka omezující agregovaná data
     * @param conditionParams Parametry pro podmínku omezující agregovaná data
     * @param readVersioned Příznak, zda se má agregace provádět i nad verzovanými záznamy
     * @param requestContext Viz {@link RequestContext}
     * @example Použití
     * const result = await dataReaderClient.getAggregations({
     *      jahodyMin: {attributeName: "Jahody", aggregationFunction: AggregationFunction.min},
     *      jahodyMax: {attributeName: "Jahody", aggregationFunction: AggregationFunction.max},
     *      jablkaAvg: {attributeName: "Jablka", aggregationFunction: AggregationFunction.avg},
     *   }, "Kosik_ovoce", "ID < 500"));
     * // result obsahuje výsledky pod klíči odpovídajících parametrů, výsledky jsou typu IBigNumber:
     * // {
     * //     jahodyMin: 12,
     * //     jahodyMax: 25,
     * //     jablkaAvg: 36,
     * // }
     */
    getAggregations(aggregationParams: IAggregationsParams, className: string, condition: string, conditionParams?: ConditionParams, readVersioned?: boolean, requestContext?: RequestContext): Promise<IAggregationsResult>;

    /**
     * Zjistí počet záznamů splňujících zadanou podmínku v rámci dané třídy.
     *
     * @param className Název třídy, pro kterou se získává počet
     * @param condition Podmínka omezující počítaná data
     * @param conditionParams Parametry pro podmínku omezující počítaná data
     * @param readVersioned Příznak, zda se mají číst i verzované záznamy
     * @param requestContext Viz {@link RequestContext}
     */
    getCount(className: string, condition: string, conditionParams?: ConditionParams, readVersioned?: boolean, requestContext?: RequestContext): Promise<number>;

    /**
     * Zjistí, zda existuje záznam splňující zadanou podmínku v rámci dané třídy.
     *
     * @param className Název třídy, pro kterou se zjišťuje existence dat
     * @param condition Podmínka omezující zkoumaná data
     * @param conditionParams Parametry pro podmínku omezující zkoumaná data
     * @param readVersioned Příznak, zda se mají číst i verzované záznamy
     * @param requestContext Viz {@link RequestContext}
     */
    exists(className: string, condition: string, conditionParams?: ConditionParams, readVersioned?: boolean, requestContext?: RequestContext): Promise<boolean>;

    /**
     * Načte aplikační data pomocí tzv. _data readeru_ v tabulkovém formátu - tedy řádky a sloupce.
     * Načítání dat _data readerem_ je mnohem výkonnější než načítání pomocí instancí. Umožňuje načítání více záznamů najednou, ale neumožňuje změnu dat.
     * Každý řádek odpovídá jednomu záznamu.
     *
     * Data se čtou pro konkrétní třídu, sloupce jsou dané seznamem atributů dané aplikační třídy.
     * Výběr dat lze omezit pomocí podmínky (viz wiki _Podmínky TescoTools_).
     * Nad načítanými daty lze vyžádat další operace jako řazení, grupování, distinct, stránkování apod.
     *
     * Vrácená data jednoho řádku (záznamu) jsou klíčovaná názvem aplikačního atributu. Pod ním je uložená struktura s hodnotou.
     * Ke čtení koncové hodnoty je doporučené používat utility třídu @see AttrValueReader.
     *
     * @param className Název třídy, pro kterou se mají číst data
     * @param attributes Seznam atributů třídy, pro které se načítají data
     * @param condition Podmínka omezující načítaná data
     * @param parameters Parametry specifikující další operace jako řazení, grupování, distinct, stránkování...
     * @param fullDataAttributes Seznam atributů u kterých nechceme ořezávat načítaná data
     * @param requestContext Viz {@link RequestContext}
     * @returns Vrací seznam načtených řádků aplikačních dat
     */
    getDataRowsWithParams(className: string, attributes: readonly string[], condition: string, parameters?: IDataRowsParams, fullDataAttributes?: string[], requestContext?: RequestContext): Promise<readonly IRowData[]>;
}
